import { Children, useRef, useState } from "react";
import type { CSSProperties, MouseEvent, ReactNode, WheelEvent } from "react";
import { Link } from "react-router-dom";

interface ScrollRowProps {
  title: string;
  viewAllLink?: string;
  expandable?: boolean;
  expandedMinCardWidth?: string;
  minItemsToExpand?: number;
  children: ReactNode;
}

/**
 * Horizontal scrolling row with title — the core layout pattern used by
 * both Jellyfin and Plex for home page sections and library recommendations.
 */
export function ScrollRow({
  title,
  viewAllLink,
  expandable = false,
  expandedMinCardWidth = "170px",
  minItemsToExpand = 6,
  children,
}: ScrollRowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ startX: number; startScrollLeft: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const childItems = Children.toArray(children);
  const canExpand = expandable && childItems.length >= minItemsToExpand;
  const trackClassName = expanded ? "scroll-row-track scroll-row-track--expanded" : "scroll-row-track";
  const trackStyle = expanded
    ? ({ "--scroll-row-expanded-min": expandedMinCardWidth } as CSSProperties)
    : undefined;

  function scroll(direction: "left" | "right") {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  }

  function handleWheel(event: WheelEvent<HTMLDivElement>) {
    if (expanded) {
      return;
    }
    const el = scrollRef.current;
    if (!el) return;

    if (Math.abs(event.deltaX) > 0) {
      return;
    }
    if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) {
      return;
    }
    if (el.scrollWidth <= el.clientWidth) {
      return;
    }

    event.preventDefault();
    el.scrollBy({ left: event.deltaY });
  }

  function handleMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (expanded) {
      return;
    }
    const el = scrollRef.current;
    if (!el) return;

    suppressClickRef.current = false;
    dragStateRef.current = {
      startX: event.clientX,
      startScrollLeft: el.scrollLeft,
      moved: false,
    };
  }

  function handleMouseMove(event: MouseEvent<HTMLDivElement>) {
    const el = scrollRef.current;
    const dragState = dragStateRef.current;
    if (!el || !dragState) return;

    const delta = event.clientX - dragState.startX;
    if (Math.abs(delta) > 4) {
      dragState.moved = true;
      suppressClickRef.current = true;
    }
    el.scrollLeft = dragState.startScrollLeft - delta;
  }

  function handleMouseUp() {
    dragStateRef.current = null;
  }

  function handleClickCapture(event: MouseEvent<HTMLDivElement>) {
    if (!suppressClickRef.current) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = false;
  }

  return (
    <section className="scroll-row">
      <div className="scroll-row-header">
        <h2 className="scroll-row-title">
          {viewAllLink ? <Link to={viewAllLink}>{title}</Link> : title}
        </h2>
        <div className="scroll-row-actions">
          {canExpand && (
            <button
              type="button"
              className="scroll-row-expand"
              aria-expanded={expanded}
              aria-label={`${expanded ? "Collapse" : "Expand"} ${title}`}
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded ? "Collapse" : "Expand"}
            </button>
          )}
          {!expanded && (
            <div className="scroll-row-nav">
              <button className="scroll-row-arrow" onClick={() => scroll("left")}>
                ‹
              </button>
              <button className="scroll-row-arrow" onClick={() => scroll("right")}>
                ›
              </button>
            </div>
          )}
        </div>
      </div>
      <div
        className={trackClassName}
        style={trackStyle}
        ref={scrollRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClickCapture={handleClickCapture}
      >
        {children}
      </div>
    </section>
  );
}
